import {
  AiScrapeFewShotExampleDto,
  AiScrapePromptConfigDto,
} from "@swagger/typescript-fetch-goflyer";
import i18n from "i18next";
import { messages } from "../../../locales/messages";

const STRING_FIELDS: Array<keyof AiScrapePromptConfigDto> = [
  "systemPrompt",
  "developerPrompt",
  "userPromptTemplate",
  "outputSchema",
  "model",
  "agentHints",
];

const NUMBER_FIELDS: Array<keyof AiScrapePromptConfigDto> = [
  "temperature",
  "topP",
  "maxOutputTokens",
  "seed",
];

const REASONING_EFFORT_VALUES = Object.values(
  AiScrapePromptConfigDto.ReasoningEffortEnum,
) as string[];
const RESPONSE_FORMAT_VALUES = Object.values(
  AiScrapePromptConfigDto.ResponseFormatEnum,
) as string[];

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === "string" && value.trim() !== "";

const isRealNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const cleanFewShotExamples = (
  examples?: Array<AiScrapeFewShotExampleDto>,
): Array<AiScrapeFewShotExampleDto> | undefined => {
  if (!Array.isArray(examples)) {
    return undefined;
  }
  const cleaned = examples.filter(
    (example) =>
      isNonEmptyString(example?.input) && isNonEmptyString(example?.output),
  );
  return cleaned.length > 0 ? cleaned : undefined;
};

/**
 * Strips empty/unset fields from a per-source prompt-config override so that
 * unset fields fall back to the global `ai_flyer_scrape.*` defaults on the
 * server. Mirrors the server-side resolution rules (string wins when
 * `trim() !== ''`, number wins when it is a real number so explicit `0`
 * overrides apply, enums must be valid, `fewShotExamples` wins when a non-empty
 * array). Returns `undefined` when nothing remains so no override is sent.
 */
export const sanitizeScrapePromptConfigOverride = (
  override?: AiScrapePromptConfigDto,
): AiScrapePromptConfigDto | undefined => {
  if (!override || typeof override !== "object") {
    return undefined;
  }

  const result: AiScrapePromptConfigDto = {};

  for (const field of STRING_FIELDS) {
    const value = override[field];
    if (isNonEmptyString(value)) {
      (result[field] as string) = value.trim();
    }
  }

  for (const field of NUMBER_FIELDS) {
    const value = override[field];
    if (isRealNumber(value)) {
      (result[field] as number) = value;
    }
  }

  if (
    typeof override.reasoningEffort === "string" &&
    REASONING_EFFORT_VALUES.includes(override.reasoningEffort as string)
  ) {
    result.reasoningEffort = override.reasoningEffort;
  }

  if (
    typeof override.responseFormat === "string" &&
    RESPONSE_FORMAT_VALUES.includes(override.responseFormat as string)
  ) {
    result.responseFormat = override.responseFormat;
  }

  const fewShotExamples = cleanFewShotExamples(override.fewShotExamples);
  if (fewShotExamples) {
    result.fewShotExamples = fewShotExamples.map((example) => ({
      input: example.input.trim(),
      output: example.output.trim(),
    }));
  }

  return Object.keys(result).length > 0 ? result : undefined;
};

/**
 * Validates a prompt-config override before save. Returns a translated error
 * message when invalid, or `null` when it is safe to submit. `outputSchema`
 * and each few-shot `output` are stored as JSON strings, so they must parse.
 */
export const validateScrapePromptConfigOverride = (
  override?: AiScrapePromptConfigDto,
): string | null => {
  if (!override || typeof override !== "object") {
    return null;
  }

  if (isNonEmptyString(override.outputSchema)) {
    try {
      JSON.parse(override.outputSchema);
    } catch {
      return i18n.t(messages.Invalid_Output_Schema_JSON());
    }
  }

  if (Array.isArray(override.fewShotExamples)) {
    for (const example of override.fewShotExamples) {
      if (isNonEmptyString(example?.output)) {
        try {
          JSON.parse(example.output);
        } catch {
          return i18n.t(messages.Invalid_Few_Shot_Output_JSON());
        }
      }
    }
  }

  return null;
};
